/* eslint-disable react/jsx-wrap-multilines */
import * as React from "react";
import { navigate, useLocation } from "@reach/router";
import styled from "styled-components";
import { SideBarAction } from "./SideBarAction";
import { ProjectsIcon } from "../../images/icons/ProjectsIcon";
import { ReportsMenuIcon } from "../../images/icons/ReportsMenuIcon";
import { ROUTES } from "../../constants/routeConstants";
import { useActionContext } from "../../utils/hooks/useActionContext";
import { useSideBarsContext } from "../../utils/hooks/useSideBarsState";
import { TeamIcon } from "../../images/icons/TeamIcon";
import { useUserRolesContext } from "../../utils/hooks/useUserRolesContext";
import { HelpMenuIcon } from "../../images/icons/HelpMenuIcon";
import { TableIcon } from "../../images/icons/TableIcon";
import { TipIcon } from "../../images/icons/TipIcon";

type SideBarActionsProps = {
  isSideBarOpen: boolean;
};

export const SideBarActions = (props: SideBarActionsProps): JSX.Element => {
  const { isSideBarOpen } = props;
  const location = useLocation();
  const { setSelectedEntity } = useActionContext();
  const { setSideDrawboardOpen } = useSideBarsContext();
  const { useUserPermissions } = useUserRolesContext();

  const isActiveLink = (route: string): boolean =>
    location.pathname.startsWith(route);

  const navigateToPage = (path: string): void => {
    navigate(path);
    setSelectedEntity(undefined);
    setSideDrawboardOpen(false);
  };

  const manageUsersIsEnabled = useUserPermissions("manageUsers");
  const reportsAreEnabled = useUserPermissions("accessReports");
  const regulatoryIsEnabled = useUserPermissions("useRegulatory");
  const readonlyIsEnabled = useUserPermissions("readonly");

  return (
    <Container>
      <SideBarAction
        name="Projects"
        isSideBarOpen={isSideBarOpen}
        icon={
          <ProjectsIcon
            isActive={
              readonlyIsEnabled && isActiveLink(ROUTES.GRID_OF_PROJECTS)
            }
          />
        }
        onClick={(): void => navigateToPage(ROUTES.GRID_OF_PROJECTS)}
        isActive={readonlyIsEnabled && isActiveLink(ROUTES.GRID_OF_PROJECTS)}
        disabled={!readonlyIsEnabled}
      />
      <SideBarAction
        name="Reports"
        isSideBarOpen={isSideBarOpen}
        icon={
          <ReportsMenuIcon
            isActive={reportsAreEnabled && isActiveLink(ROUTES.REPORTS)}
          />
        }
        onClick={(): void => navigateToPage(ROUTES.REPORTS)}
        isActive={reportsAreEnabled && isActiveLink(ROUTES.REPORTS)}
        disabled={!reportsAreEnabled}
      />
      <SideBarAction
        name="Regulatory"
        isSideBarOpen={isSideBarOpen}
        icon={
          <TipIcon
            isActive={
              regulatoryIsEnabled && location.pathname === ROUTES.REGULATORY
            }
          />
        }
        onClick={(): void => navigateToPage(ROUTES.REGULATORY)}
        isActive={
          regulatoryIsEnabled && location.pathname === ROUTES.REGULATORY
        }
        disabled={!regulatoryIsEnabled}
      />
      <SideBarAction
        name="eCTD mapping"
        isSideBarOpen={isSideBarOpen}
        icon={
          <TipIcon
            isActive={
              regulatoryIsEnabled &&
              isActiveLink(ROUTES.REGULATORY_ECTD_MAPPING)
            }
          />
        }
        onClick={(): void => navigateToPage(ROUTES.REGULATORY_ECTD_MAPPING)}
        isActive={
          regulatoryIsEnabled && isActiveLink(ROUTES.REGULATORY_ECTD_MAPPING)
        }
        disabled={!regulatoryIsEnabled}
      />
      <SideBarAction
        name="Project Users"
        isSideBarOpen={isSideBarOpen}
        icon={
          <TeamIcon
            isActive={manageUsersIsEnabled && isActiveLink(ROUTES.MANAGE_TEAMS)}
          />
        }
        onClick={(): void => navigateToPage(ROUTES.MANAGE_TEAMS)}
        isActive={manageUsersIsEnabled && isActiveLink(ROUTES.MANAGE_TEAMS)}
        disabled={!manageUsersIsEnabled}
      />
      <SideBarAction
        name="Groups"
        isSideBarOpen={isSideBarOpen}
        icon={
          <TeamIcon
            isActive={
              manageUsersIsEnabled && isActiveLink(ROUTES.MANAGE_GROUPS)
            }
          />
        }
        onClick={(): void => navigateToPage(ROUTES.MANAGE_GROUPS)}
        isActive={manageUsersIsEnabled && isActiveLink(ROUTES.MANAGE_GROUPS)}
        disabled={!manageUsersIsEnabled}
      />
      <SideBarAction
        name="Users"
        isSideBarOpen={isSideBarOpen}
        icon={
          <TeamIcon
            isActive={manageUsersIsEnabled && isActiveLink(ROUTES.MANAGE_USERS)}
          />
        }
        onClick={(): void => navigateToPage(ROUTES.MANAGE_USERS)}
        isActive={manageUsersIsEnabled && isActiveLink(ROUTES.MANAGE_USERS)}
        disabled={!manageUsersIsEnabled}
      />
      <SideBarAction
        name="Dashboard"
        isSideBarOpen={isSideBarOpen}
        icon={
          <HelpMenuIcon
            isActive={readonlyIsEnabled && isActiveLink(ROUTES.DASHBOARD)}
          />
        }
        onClick={(): void => navigateToPage(ROUTES.DASHBOARD)}
        isActive={readonlyIsEnabled && isActiveLink(ROUTES.DASHBOARD)}
        disabled={!readonlyIsEnabled}
      />
      <SideBarAction
        name="Tasks"
        isSideBarOpen={isSideBarOpen}
        icon={
          <TableIcon
            isActive={readonlyIsEnabled && isActiveLink(ROUTES.TASKS)}
          />
        }
        onClick={(): void => navigateToPage(ROUTES.TASKS)}
        isActive={readonlyIsEnabled && isActiveLink(ROUTES.TASKS)}
        disabled={!readonlyIsEnabled}
      />
    </Container>
  );
};

const Container = styled.div`
  & > div {
    margin-bottom: 8px;
  }
`;
